import { RELAY_TTL_DAYS } from '../constants';
import type { AccountType } from '../accounts';
import { approvalSignatureValid, consentTermsOf, type PublicConsent } from '../consent';
import { fromBase64Url } from '../encoding';
import { documentSigner, verifyDocumentSignature } from '../fhir/signature';
import type { Bundle } from '../fhir/types';
import { decodeEnvelopeHeader, type EnvelopeHeader, type EnvelopeKind } from '../envelope';
import { peerFromDirectory, type DeviceKeys } from '../keys';
import type { Role } from '../roles';
import { jsonBytes, openEnvelope, parseJsonBytes, sealEnvelope } from '../seal';
import { answerPracticeRequest } from './engine';
import { practiceRequestSchema, type PracticeOp } from './protocol';
import type { RecordStore } from './records';
import { deliveryPayloadSchema, takePrescription } from './deliver';
import { noticeOf, type NoticeCursor } from './notices';
import { ShareRefusal, documentsUnder, historySharePayloadSchema, patientsOfAccount, sealHistoryShares, takeShare } from './share';

const DAY_MS = 24 * 60 * 60 * 1000;

/** The kinds of envelope a record holder takes from its inbox. Prescriptions reach only pharmacies' record holders. */
export const HOLDER_KINDS = ['practice_request', 'history_share', 'prescription'] as const satisfies readonly EnvelopeKind[];

/** An envelope as the relay delivers it. */
export interface RelayedEnvelope {
  id: string;
  kind: string;
  header: string;
  ciphertext: string;
  signature: string;
  senderAccountId: string;
  senderDevice: string;
}

/** A member with their roles and active devices, as the server tells the record holder. */
export interface HolderMemberView {
  accountId: string;
  accountType: AccountType;
  roles: Role[];
  devices: { id: string; encryptionKey: string; signingKey: string }[];
}

/** A device as the key directory describes it, with its owner. */
export interface DirectoryEntry {
  id: string;
  encryptionKey: string;
  signingKey: string;
  accountId: string;
  /** The owner's account type, which tells a doctor's device from a patient's. */
  accountType?: AccountType | null;
  revokedAt: string | null;
}

type Wire = { header: string; ciphertext: string; signature: string };

/** How the holder reaches the server. The hub and the app each provide one. */
export interface HolderTransport {
  /** Envelopes waiting for this holder, of the kinds in HOLDER_KINDS, oldest first. */
  inbox(): Promise<RelayedEnvelope[]>;
  ack(ids: string[]): Promise<void>;
  send(envelopes: Wire[]): Promise<void>;
  members(): Promise<HolderMemberView[]>;
  /** A consent naming this practice, or null when there is none the holder may read. */
  consent(id: string): Promise<PublicConsent | null>;
  /** Approved, unexpired consents that name this practice as a source. */
  sourceConsents(): Promise<PublicConsent[]>;
  /** A device's public keys and owner, or null when the directory has no such device. */
  device(id: string): Promise<DirectoryEntry | null>;
  /** Another practice's active record holder devices. */
  holders(practiceId: string): Promise<{ id: string; encryptionKey: string; signingKey: string }[]>;
  /** An account's active devices, for notices to a patient or a doctor. Empty when there are none or the account is gone. */
  accountDevices(accountId: string): Promise<{ id: string; encryptionKey: string; signingKey: string }[]>;
}

/** Envelope IDs already handled, kept encrypted, so a captured envelope cannot be replayed. */
export interface SeenEnvelopes {
  has(id: string): Promise<boolean>;
  add(id: string, createdAt: string): Promise<void>;
}

/** Consents this holder has already shared under, kept encrypted until they expire, so each is fulfilled once. */
export interface SharedConsents {
  has(consentId: string): Promise<boolean>;
  add(consentId: string, expiresAt: string): Promise<void>;
}

type DropReason =
  | 'replayed'
  | 'unknown_sender'
  | 'unreadable'
  | 'misaddressed'
  | 'too_old'
  | 'invalid_request'
  | 'no_consent'
  | 'bad_signature'
  | 'refused';

/** What the holder reports for its log. IDs, counts and outcomes only, never content. */
export type HolderEvent =
  | { type: 'answered'; envelopeId: string; op: PracticeOp; ok: boolean }
  | { type: 'received'; envelopeId: string; consentId: string }
  | { type: 'prescribed'; envelopeId: string; forwarded: boolean }
  | { type: 'shared'; consentId: string; records: number }
  | { type: 'not_shared'; consentId: string; reason: 'bad_signature' | 'no_holder' }
  | { type: 'notified'; recordId: string; devices: number }
  | { type: 'dropped'; envelopeId: string; reason: DropReason };

export interface HolderOptions {
  practiceId: string;
  keys: DeviceKeys;
  store: RecordStore;
  transport: HolderTransport;
  seen: SeenEnvelopes;
  shared: SharedConsents;
  /** How far dispensing notices have gone. A holder without one sends none. */
  notices?: NoticeCursor;
  now?: () => Date;
  onEvent?: (event: HolderEvent) => void;
}

/** Runs a task one at a time. A call during a run makes it go round once more. */
class Runner {
  private running: Promise<void> | null = null;
  private again = false;

  constructor(private readonly once: () => Promise<boolean>) {}

  run(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        let more;
        do {
          this.again = false;
          more = await this.once();
        } while (more || this.again);
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  async idle(): Promise<void> {
    await this.running?.catch(() => undefined);
  }
}

/**
 * A practice's record holder on the relay, on a hub or a solo doctor's or
 * pharmacist's device.
 *
 * It answers members' practice requests: it checks that the sender is an
 * active member's active device, answers with answerPracticeRequest under the
 * member's roles, and replies encrypted to that device alone. The member list
 * is fetched again for every batch, so a removal or a role change applies to
 * the next request even if the holder missed the alert.
 *
 * It takes in history shares sent to its practice: each needs an approved,
 * unexpired consent from this practice that covers the document, signed by
 * the patient's own device, and a sender that is the patient or a source the
 * patient named.
 *
 * It shares its own records when a patient approves a consent naming its
 * practice as a source, once per consent.
 *
 * As a pharmacy's record holder, it files prescriptions sent by a linked
 * doctor or forwarded by a patient, once a doctor's device has been shown to
 * have signed them. For every dispensing record its members write, it sends
 * a notice to the patient's and the prescribing doctor's devices.
 */
export class HolderService {
  private members = new Map<string, HolderMemberView>();
  private readonly signatures = new Map<string, boolean>();
  private readonly inbox = new Runner(async () => (await this.drainOnce()) > 0);
  private readonly sharing = new Runner(async () => {
    await this.shareOnce();
    return false;
  });
  private readonly notifying = new Runner(async () => {
    await this.notifyOnce();
    return false;
  });

  constructor(private readonly options: HolderOptions) {}

  /** Handles everything waiting in the inbox. */
  drain(): Promise<void> {
    return this.inbox.run();
  }

  /** Shares this practice's records under approved consents that name it as a source and are not yet fulfilled. */
  shareApproved(): Promise<void> {
    return this.sharing.run();
  }

  /**
   * Sends notices for dispensing records written since the last run, to the
   * patient's and the prescribing doctor's devices. A failure leaves the rest
   * for the next run, so every record is notified at least once.
   */
  notifyDispensed(): Promise<void> {
    return this.notifying.run();
  }

  /** Waits for runs in progress, without starting one. */
  async idle(): Promise<void> {
    await Promise.all([this.inbox.idle(), this.sharing.idle(), this.notifying.idle()]);
  }

  private now(): Date {
    return this.options.now?.() ?? new Date();
  }

  /** Handles one page of the inbox. Returns how many envelopes it handled, so draining stops when none are left. */
  private async drainOnce(): Promise<number> {
    const envelopes = (await this.options.transport.inbox()).filter((e) => (HOLDER_KINDS as readonly string[]).includes(e.kind));
    if (envelopes.length === 0) return 0;
    if (envelopes.some((e) => e.kind === 'practice_request')) {
      const list = await this.options.transport.members();
      this.members = new Map(list.map((member) => [member.accountId, member]));
    }
    // Consents are read once per batch: one share of many documents needs one look.
    const consents = new Map<string, PublicConsent | null>();
    for (const envelope of envelopes) {
      if (envelope.kind === 'practice_request') await this.handle(envelope);
      else if (envelope.kind === 'prescription') await this.takePrescription(envelope);
      else await this.intake(envelope, consents);
    }
    return envelopes.length;
  }

  private async drop(envelope: RelayedEnvelope, reason: DropReason): Promise<void> {
    await this.options.transport.ack([envelope.id]);
    this.options.onEvent?.({ type: 'dropped', envelopeId: envelope.id, reason });
  }

  private tooOld(header: EnvelopeHeader): boolean {
    return Date.parse(header.createdAt) < this.now().getTime() - RELAY_TTL_DAYS * DAY_MS;
  }

  private async handle(envelope: RelayedEnvelope): Promise<void> {
    const { keys, practiceId, seen, store, transport } = this.options;
    if (await seen.has(envelope.id)) return this.drop(envelope, 'replayed');

    const member = this.members.get(envelope.senderAccountId);
    const device = member?.devices.find((d) => d.id === envelope.senderDevice);
    if (!member || !device) return this.drop(envelope, 'unknown_sender');

    const sender = peerFromDirectory(device);
    let opened;
    try {
      opened = await openEnvelope({ recipient: keys, envelope, sender });
    } catch {
      return this.drop(envelope, 'unreadable');
    }
    const { header, plaintext } = opened;
    if (header.kind !== 'practice_request' || header.to.practice !== practiceId || header.from.practice !== undefined) {
      return this.drop(envelope, 'misaddressed');
    }
    if (this.tooOld(header)) return this.drop(envelope, 'too_old');

    let parsed;
    try {
      parsed = practiceRequestSchema.safeParse(parseJsonBytes(plaintext));
    } catch {
      parsed = null;
    }
    if (!parsed?.success) return this.drop(envelope, 'invalid_request');

    const response = await answerPracticeRequest(store, { accountId: member.accountId, roles: member.roles }, parsed.data, this.now());
    const reply = await sealEnvelope({
      kind: 'practice_response',
      sender: keys,
      recipient: sender,
      plaintext: jsonBytes(response),
      fromPractice: practiceId,
    });
    // Reply, remember, then acknowledge. If the reply fails, the request stays
    // in the relay and is answered again, and a repeated write is recognised.
    // Remembering before acknowledging leaves no gap in the replay guard.
    await transport.send([reply.wire]);
    await seen.add(envelope.id, header.createdAt);
    await transport.ack([envelope.id]);
    this.options.onEvent?.({ type: 'answered', envelopeId: envelope.id, op: parsed.data.op, ok: response.ok });
  }

  /** True when the patient's own device signed this approval. Remembered per approval. */
  private async approvedByPatient(consent: PublicConsent): Promise<boolean> {
    const approval = consent.approval;
    if (!approval) return false;
    const key = `${consent.id}:${approval.signature}`;
    const known = this.signatures.get(key);
    if (known !== undefined) return known;
    const device = await this.options.transport.device(approval.deviceId);
    const valid =
      device !== null && device.accountId === consent.patientId && (await approvalSignatureValid(consent, fromBase64Url(device.signingKey)));
    this.signatures.set(key, valid);
    return valid;
  }

  /** Takes in one document shared with this practice, after checking the consent, the sender and the document. */
  private async intake(envelope: RelayedEnvelope, consents: Map<string, PublicConsent | null>): Promise<void> {
    const { keys, practiceId, seen, store, transport } = this.options;
    if (await seen.has(envelope.id)) return this.drop(envelope, 'replayed');
    let header: EnvelopeHeader;
    try {
      header = decodeEnvelopeHeader(fromBase64Url(envelope.header));
    } catch {
      return this.drop(envelope, 'unreadable');
    }
    if (header.kind !== 'history_share' || header.to.practice !== practiceId || header.to.device !== keys.id || !header.consent || !header.hiType) {
      return this.drop(envelope, 'misaddressed');
    }
    if (this.tooOld(header)) return this.drop(envelope, 'too_old');

    if (!consents.has(header.consent)) consents.set(header.consent, await transport.consent(header.consent));
    const consent = consents.get(header.consent);
    // A consent revoked or expired while the share waited in the relay stops it here.
    if (
      !consent?.approval ||
      consent.status !== 'approved' ||
      Date.parse(consent.expiresAt) <= this.now().getTime() ||
      consent.requesterPracticeId !== practiceId ||
      !consent.hiTypes.includes(header.hiType)
    ) {
      return this.drop(envelope, 'no_consent');
    }
    const fromPractice = header.from.practice;
    if (fromPractice !== undefined && !consent.approval.sources.includes(fromPractice)) return this.drop(envelope, 'unknown_sender');
    const device = await transport.device(header.from.device);
    if (!device || device.revokedAt || (fromPractice === undefined && device.accountId !== consent.patientId)) {
      return this.drop(envelope, 'unknown_sender');
    }
    if (!(await this.approvedByPatient(consent))) return this.drop(envelope, 'no_consent');

    let parsed;
    try {
      const opened = await openEnvelope({ recipient: keys, envelope, sender: peerFromDirectory(device) });
      parsed = historySharePayloadSchema.safeParse(parseJsonBytes(opened.plaintext));
    } catch {
      return this.drop(envelope, 'unreadable');
    }
    if (!parsed.success || parsed.data.consentId !== consent.id || parsed.data.record.hiType !== header.hiType) {
      return this.drop(envelope, 'invalid_request');
    }
    const practiceName = consent.practices.find((p) => p.id === fromPractice)?.name;
    try {
      await takeShare(store, {
        payload: parsed.data,
        terms: consentTermsOf(consent),
        sender: fromPractice === undefined ? { kind: 'patient' } : { kind: 'practice', practiceId: fromPractice, ...(practiceName ? { practiceName } : {}) },
        senderId: device.accountId,
        now: this.now(),
      });
    } catch (err) {
      if (err instanceof ShareRefusal) return this.drop(envelope, 'refused');
      throw err;
    }
    await seen.add(envelope.id, header.createdAt);
    await transport.ack([envelope.id]);
    this.options.onEvent?.({ type: 'received', envelopeId: envelope.id, consentId: consent.id });
  }

  /**
   * Files one prescription sent to this pharmacy. The relay has checked that
   * a doctor sender links this pharmacy. Here the holder checks that the
   * document is signed by a doctor's active device: the sender's own, when a
   * doctor sends it, or any doctor's, when a patient forwards it.
   */
  private async takePrescription(envelope: RelayedEnvelope): Promise<void> {
    const { keys, practiceId, seen, store, transport } = this.options;
    if (await seen.has(envelope.id)) return this.drop(envelope, 'replayed');
    let header: EnvelopeHeader;
    try {
      header = decodeEnvelopeHeader(fromBase64Url(envelope.header));
    } catch {
      return this.drop(envelope, 'unreadable');
    }
    if (header.kind !== 'prescription' || header.to.practice !== practiceId || header.to.device !== keys.id || header.from.practice !== undefined) {
      return this.drop(envelope, 'misaddressed');
    }
    if (this.tooOld(header)) return this.drop(envelope, 'too_old');
    const sender = await transport.device(header.from.device);
    if (!sender || sender.revokedAt || (sender.accountType !== 'doctor' && sender.accountType !== 'patient')) return this.drop(envelope, 'unknown_sender');

    let parsed;
    try {
      const opened = await openEnvelope({ recipient: keys, envelope, sender: peerFromDirectory(sender) });
      parsed = deliveryPayloadSchema.safeParse(parseJsonBytes(opened.plaintext));
    } catch {
      return this.drop(envelope, 'unreadable');
    }
    if (!parsed.success || parsed.data.record.hiType !== 'Prescription') return this.drop(envelope, 'invalid_request');

    const bundle = parsed.data.record.resource as unknown as Bundle;
    const signer = documentSigner(bundle);
    const forwarded = sender.accountType === 'patient';
    if (!signer || (!forwarded && signer.deviceId !== sender.id)) return this.drop(envelope, 'bad_signature');
    const signerDevice = signer.deviceId === sender.id ? sender : await transport.device(signer.deviceId);
    if (
      !signerDevice ||
      signerDevice.revokedAt ||
      signerDevice.accountType !== 'doctor' ||
      !(await verifyDocumentSignature(bundle, fromBase64Url(signerDevice.signingKey)))
    ) {
      return this.drop(envelope, 'bad_signature');
    }
    try {
      await takePrescription(store, { payload: parsed.data, senderId: sender.accountId, forwarded, now: this.now() });
    } catch (err) {
      if (err instanceof ShareRefusal) return this.drop(envelope, 'refused');
      throw err;
    }
    await seen.add(envelope.id, header.createdAt);
    await transport.ack([envelope.id]);
    this.options.onEvent?.({ type: 'prescribed', envelopeId: envelope.id, forwarded });
  }

  private async notifyOnce(): Promise<void> {
    const { notices, store } = this.options;
    if (!notices) return;
    const from = await notices.get();
    let done = from;
    try {
      for (const entry of await store.since(from)) {
        if (entry.kind === 'dispense') await this.notify(entry.id);
        done = entry.seq;
      }
    } finally {
      if (done > from) await notices.set(done);
    }
  }

  /**
   * Sends one dispensing record, with the prescription it answers, to the
   * patient's devices when the practice's patient record names their account,
   * and to the devices of the doctor whose device signed the prescription.
   */
  private async notify(recordId: string): Promise<void> {
    const { keys, practiceId, store, transport } = this.options;
    const dispense = await store.get(recordId);
    const prescription = dispense?.prescriptionId ? await store.get(dispense.prescriptionId) : null;
    if (!dispense || prescription?.hiType !== 'Prescription') return;
    const patient = await store.get(dispense.patientId);
    const signer = documentSigner(prescription.resource as unknown as Bundle);
    const doctor = signer ? await transport.device(signer.deviceId) : null;
    const accounts = new Set<string>();
    if (patient?.accountId) accounts.add(patient.accountId);
    if (doctor?.accountType === 'doctor') accounts.add(doctor.accountId);
    const recipients: { id: string; encryptionKey: string; signingKey: string }[] = [];
    for (const accountId of accounts) recipients.push(...(await transport.accountDevices(accountId)));
    if (recipients.length > 0) {
      const plaintext = jsonBytes(noticeOf(dispense, prescription, practiceId));
      const wires: Wire[] = [];
      for (const recipient of recipients) {
        wires.push((await sealEnvelope({ kind: 'dispense_notice', sender: keys, recipient: peerFromDirectory(recipient), plaintext, fromPractice: practiceId })).wire);
      }
      await transport.send(wires);
    }
    this.options.onEvent?.({ type: 'notified', recordId, devices: recipients.length });
  }

  private async shareOnce(): Promise<void> {
    for (const consent of await this.options.transport.sourceConsents()) {
      if (!(await this.options.shared.has(consent.id))) await this.shareUnder(consent);
    }
  }

  /**
   * Sends the records this practice made about the patient that the consent
   * covers, encrypted to each record holder device of the requesting practice.
   * The relay checks the consent again for every envelope.
   */
  private async shareUnder(consent: PublicConsent): Promise<void> {
    const { keys, practiceId, shared, store, transport } = this.options;
    if (consent.status !== 'approved' || !consent.approval?.sources.includes(practiceId)) return;
    if (Date.parse(consent.expiresAt) <= this.now().getTime()) return;
    if (!(await this.approvedByPatient(consent))) {
      this.options.onEvent?.({ type: 'not_shared', consentId: consent.id, reason: 'bad_signature' });
      return;
    }
    const terms = consentTermsOf(consent);
    const patients = await patientsOfAccount(store, consent.patientId);
    const documents = await documentsUnder(
      store,
      patients.map((p) => p.id),
      terms,
    );
    if (documents.length > 0) {
      const holders = (await transport.holders(consent.requesterPracticeId)).map(peerFromDirectory);
      if (holders.length === 0) {
        // Tried again on the next round, until the requesting practice has a record holder or the consent ends.
        this.options.onEvent?.({ type: 'not_shared', consentId: consent.id, reason: 'no_holder' });
        return;
      }
      await transport.send(await sealHistoryShares({ sender: keys, fromPractice: practiceId, terms, documents, recipients: holders }));
    }
    await shared.add(consent.id, consent.expiresAt);
    this.options.onEvent?.({ type: 'shared', consentId: consent.id, records: documents.length });
  }
}
